import { SubtitleCue } from "./types";

/**
 * Converts SRT time format (HH:MM:SS,mmm) to seconds
 */
function parseSrtTime(timeString: string): number {
  const [time, milliseconds] = timeString.split(",");
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds + Number(milliseconds) / 1000;
}

/**
 * Parses an SRT subtitle file content into an array of SubtitleCue objects
 */
export function parseSrt(srtContent: string, offsetMs: number = 0): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  const offsetSeconds = offsetMs / 1000;
  
  // Split by double newlines to get individual subtitle blocks
  const blocks = srtContent.trim().split(/\n\s*\n/);
  
  for (const block of blocks) {
    const lines = block.trim().split(/\n/);
    if (lines.length < 3) continue; // Need at least: index, time, text
    
    // Skip the index line (first line)
    const timeLine = lines[1];
    const textLines = lines.slice(2);
    
    // Parse time line: "00:00:00,200 --> 00:00:01,439"
    const timeMatch = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);
    if (!timeMatch) continue;
    
    const startTime = parseSrtTime(timeMatch[1]) + offsetSeconds;
    const endTime = parseSrtTime(timeMatch[2]) + offsetSeconds;
    
    // Join text lines and clean up
    const text = textLines
      .join(" ")
      .replace(/<[^>]+>/g, "") // Remove HTML tags
      .trim();
    
    if (text) {
      cues.push({
        start: startTime,
        end: endTime,
        text: text,
      });
    }
  }
  
  return cues;
}

/**
 * Loads and parses an SRT file from a URL
 */
export async function loadSrtFile(path: string, offsetMs: number = 0): Promise<SubtitleCue[]> {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to load SRT file: ${response.statusText}`);
    }
    const content = await response.text();
    return parseSrt(content, offsetMs);
  } catch (error) {
    console.error("Error loading SRT file:", error);
    return [];
  }
}

